import classnames from 'classnames';
import type React from 'react';
import InputMask from 'react-input-mask';
import NumberFormat from 'react-number-format';
import { Button, Input } from 'reactstrap';
import eyeIcon from '@/shared/ui/icons/Components/Status/eye-icon.svg';
import eyeSlashIcon from '@/shared/ui/icons/Components/Status/eye-slash-icon.svg';
import { IconButton } from '@/shared/ui';
import useTextInput from './useTextInput';

export type TextInputProps = {
  className?: string;
  isDateMask?: boolean;
  isFourDigitsMask?: boolean;
  isPhoneNumberMask?: boolean;
  isPasswordField?: boolean;
  name: string;
  type?: string;
  placeholder?: string;
  label?: string;
  value?: string | number;
  disabled?: boolean;
  isError?: boolean;
  errorMessage?: string;
  isWarning?: boolean;
  warningMessage?: string;
  warningLinkText?: string;
  warningLink?: string;
  onChange?: (name: string, value: any, sizeOrIndex?: number) => void;
  optionCheckOut?: React.ReactNode;
  classNameLabel?: string;
  id?: string;
  onKeyDown?: (e: any) => void;
  isNumberFormat?: boolean;
  messageBoxContent?: string;
  linkText?: string;
  handleClickLinkText?: (e: React.MouseEvent) => void;
  cautionText?: React.ReactNode;
  index?: number;
  suffix?: string;
  customSuffix?: string;
  decimalSeparator?: string | boolean;
  decimalScale?: number;
  maxLength?: number;
  // used in JSX below but never declared in the original PropTypes
  size?: number;
  shouldChangeSize?: boolean;
  onBlur?: (e: React.FocusEvent) => void;
  ariaLabel?: string;
};

const TextInput = ({
  className,
  isDateMask = false,
  isFourDigitsMask = false,
  isPhoneNumberMask = false,
  isPasswordField = false,
  name,
  type = 'text',
  placeholder = '',
  label = '',
  value = '',
  disabled = false,
  isError = false,
  errorMessage = '',
  isWarning = false,
  warningMessage = '',
  warningLinkText = '',
  warningLink = '',
  onChange = () => {},
  optionCheckOut,
  classNameLabel = '',
  id,
  onKeyDown = () => {},
  isNumberFormat = false,
  messageBoxContent = '',
  linkText = '',
  handleClickLinkText = () => {},
  cautionText = null,
  index,
  suffix,
  customSuffix,
  decimalSeparator = '.',
  decimalScale = 1,
  maxLength,
  size,
  shouldChangeSize,
  onBlur,
  ariaLabel,
}: TextInputProps) => {
  const {
    inputElem,
    state,
    handleChange,
    onChangeNumberFormat,
    toggleDisplayPassword,
    onClickWarningLink,
  } = useTextInput({
    name,
    onChange,
    index,
    size,
    shouldChangeSize,
    id,
    onKeyDown,
    isPasswordField,
  });
  // No htmlFor/label association here (would make the label text
  // click-to-focus the input, a UI behaviour change) — the visible label
  // doubles as the accessible name via aria-label instead.
  const resolvedAriaLabel = ariaLabel || label || undefined;

  return (
    <div
      id={`text-input-wrapper-${id}`}
      className={classnames('custom-text-input', className, suffix ? '--suffix' : '')}
    >
      {!!messageBoxContent && messageBoxContent}

      {!!label && (
        <div className={classnames('custom-text-input__label', classNameLabel)}>{label}</div>
      )}

      {optionCheckOut}

      {isDateMask || isFourDigitsMask || isPhoneNumberMask ? (
        <InputMask
          className={classnames(
            'custom-text-input__input',
            'w-100',
            type === 'textarea' ? '--textarea' : '',
            isError ? '--error' : '',
          )}
          name={name}
          type={isDateMask || isFourDigitsMask ? 'text' : 'tel'}
          placeholder={placeholder}
          mask={isDateMask ? '99/99/9999' : isFourDigitsMask ? '9999' : '999-999-9999'}
          maskChar={null}
          value={value}
          onChange={handleChange}
          id={id}
          aria-label={resolvedAriaLabel}
          onKeyDown={onKeyDown}
          disabled={disabled}
          maxLength={maxLength}
        />
      ) : isNumberFormat ? (
        <div className="relative">
          <NumberFormat
            id={id}
            aria-label={resolvedAriaLabel}
            className={classnames('custom-text-input__input w-100', isError ? '--error' : '')}
            style={{ paddingRight: suffix ? suffix.length * 10 : '1rem' }}
            placeholder={placeholder}
            name={name}
            onValueChange={({ value }: any, { event }: any) => {
              onChangeNumberFormat(value, event);
            }}
            value={value}
            disabled={disabled}
            allowNegative={false}
            isNumericString
            // react-number-format types decimalSeparator as string only, but
            // PropTypes allowed bool too (e.g. `false`) — pre-existing.
            decimalSeparator={decimalSeparator as any}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            decimalScale={decimalScale}
            getInputRef={(el: any) => {
              inputElem.current = el;
            }}
          />
          {suffix && (
            <div
              className={classnames('custom-text-input__suffix')}
              style={customSuffix ? { fontStyle: 'italic', fontWeight: 400 } : undefined}
            >
              {suffix}
            </div>
          )}
        </div>
      ) : (
        <Input
          className={classnames(
            'custom-text-input__input',
            type === 'textarea' ? '--textarea' : '',
            isError ? '--error' : '',
          )}
          id={id}
          aria-label={resolvedAriaLabel}
          name={name}
          type={(state.isShowPassword ? type : 'password') as any}
          disabled={disabled}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          readOnly={false}
          maxLength={maxLength}
        />
      )}

      {!!value && isPasswordField && (
        <div className={classnames('custom-text-input__icon-container', !!label && '--has-label')}>
          <IconButton
            className="--has-no-pseudo-classes"
            onClick={toggleDisplayPassword}
            iconComponent={
              <img
                className="custom-text-input__icon-container__icon"
                src={state.isShowPassword ? eyeIcon : eyeSlashIcon}
                alt="Eye icon"
              />
            }
          />
        </div>
      )}

      {isError && !!errorMessage && (
        <div className={linkText ? 'flex justify-between items-center' : undefined}>
          <div className="custom-text-input__error-message">{errorMessage}</div>
          {!!linkText && (
            <Button
              className="custom-select-input__warning-button"
              color="link"
              onClick={handleClickLinkText}
            >
              {linkText}
            </Button>
          )}
        </div>
      )}
      {!!cautionText && cautionText}
      {isWarning && (
        <div className="custom-text-input__warning-container">
          <div className="custom-text-input__warning-message">{warningMessage}</div>
          <Button
            color="link"
            onClick={() => {
              onClickWarningLink(warningLink);
            }}
          >
            {warningLinkText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TextInput;
